import { useState, useContext, useEffect } from "react";
import { ExamTemplateMappingInfo } from "../../types/result";
import { useQuery } from "@tanstack/react-query";
import { UserContext } from "../../provider/UserProvider";
import { GetUserPackageInfoByUserId } from "../../services/authService";
import { UserPackageInfoModel, UserExamInfoModel } from "../../types/user";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../utils/dateUtils";
import { useLoader } from "../../provider/LoaderProvider";
import { getExamInfoByExamId } from "../../services/examService";
import AvailablePackagesSection from "../packages/AvailablePackagesSection";
import { Award, CheckCircle, Clock, Play, Users, Zap } from "lucide-react";
import { GetExamTemplateMappings } from "../../services/resultService";

const HomeComponent = () => {
  const { userAuth } = useContext(UserContext);

  const navigate = useNavigate();
  const { setLoading } = useLoader();


  const [currentPackage, setCurrentPackage] = useState<UserPackageInfoModel | null>(null);
  const [completedPackages, setCompletedPackages] = useState<UserPackageInfoModel[]>([]);
  const [showAllCompleted, setShowAllCompleted] = useState<boolean>(false);


  let UserId = userAuth?.userId || 0;


  const { data: UserPackageInfoModel, isSuccess, isLoading: isLoadingPackages, isFetching } = useQuery({
    queryKey: ["userPackages"],
    queryFn: async () => {

      const data = await GetUserPackageInfoByUserId(UserId);
      return data ?? null; // Ensure we never return undefined
    },
    enabled: !!UserId && UserId > 0, // Only fetch if UserId is valid and positive
  });

  // Show loader while fetching packages
  useEffect(() => {
    const loading = isLoadingPackages || isFetching;
    setLoading(loading, "dashboard-packages");

  }, [isLoadingPackages, isFetching, setLoading]);

  useEffect(() => {

    // Only process if query was successful
    if (isSuccess) {
      if (UserPackageInfoModel && Array.isArray(UserPackageInfoModel) && UserPackageInfoModel.length > 0) {
        const newCurrentPackage = UserPackageInfoModel.find(pkg => !pkg.isCompleted) || null;
        const newCompletedPackages = UserPackageInfoModel.filter(pkg => pkg.isCompleted);

        setCurrentPackage(newCurrentPackage);
        setCompletedPackages(newCompletedPackages);


      } else {
        // No packages found or empty array
        setCurrentPackage(null);
        setCompletedPackages([]);
      }
    }
  }, [UserPackageInfoModel, isSuccess]);
  const handleResult = async (packageId: number) => {
    setLoading(true);

    try {
      // Find the package (current or completed)
      const pkg =
        currentPackage?.id === packageId
          ? currentPackage
          : completedPackages.find(p => p.id === packageId);

      if (!pkg || !pkg.exams || pkg.exams.length === 0) {

        return;
      }

      // Pick any completed exam (first is enough)
      const completedExam = pkg.exams.find(e => e.isCompleted && e.examProgressId);

      if (!completedExam) {

        return;
      }

      // 🔑 CHECK TEMPLATE MAPPING
      const mappings = await GetExamTemplateMappings();
      // Exam 9 is legacy => force resultnew
      const isLegacyExam = completedExam.examId === 9;
      const hasTemplate = mappings?.some((m: ExamTemplateMappingInfo) => m.examId === completedExam.examId);

      if (hasTemplate && !isLegacyExam) {
        // PRIORITY: If a template is mapped (and not legacy), go to exam-summary
        navigate("/exam-summary", {
          state: { examProgressId: completedExam.examProgressId }
        });
      } else {
        // FALLBACK / LEGACY
        navigate("/resultnew", {
          state: { packageId }
        });
      }

    } catch (error) {
      console.log(error);

    } finally {
      setLoading(false);
    }
  };

  const handleQuizClick = async (examId: number) => {
    console.log("Start Exam clicked for examId:", examId);
    console.time("QuizStartTimer");
    setLoading(true, "quiz-transition");
    console.log("Loader turned ON (quiz-transition)");
    
    // Tiny delay to ensure the loader paints before the heavy API call
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      // Use the current package instead of looping through all packages
      if (!currentPackage) {
        console.log("No active package found.");
        setLoading(false, "quiz-transition");
        return;
      }

      // Find the exam from the current package
      let selectedExam: UserExamInfoModel | null = null;
      let userPackageId: number = currentPackage.userPackageId;
      let packageId: number = currentPackage.packageId;
      let examProgressId: number = 0;

      const foundExam = currentPackage.exams.find(exam => exam.examId === examId);
      if (foundExam) {
        selectedExam = foundExam;
        examProgressId = foundExam.examProgressId;
      }

      if (!selectedExam) {
        console.log("Exam not found in current package.");
        setLoading(false, "quiz-transition");
        return;
      }

      // Fetch exam sections
      const fetchedExam = await getExamInfoByExamId(examId);
      const sections = fetchedExam?.sections || [];

      // Extract all questions from sections
      const examQuestions = sections.flatMap(section => section.questions) || [];

      console.timeEnd("QuizStartTimer");
      console.log("Navigating to /quiz with questions:", examQuestions.length);

      // Navigate to the quiz page
      navigate('/quiz', {
        state: {
          userId: UserId,
          examId: selectedExam.examId,
          examName: selectedExam.examName,
          timeLimit: selectedExam.timeLimit,
          userExamProgressId: examProgressId,
          userPackageId: userPackageId,
          packageId: packageId,
          examQuestions: examQuestions,
          sections: sections,
        },
      });
    } catch (error) {
      console.log("Error fetching exam details:", error);
      setLoading(false, "quiz-transition");
    }
  };


  const ExamStatus = ({
    exam,
    onStartExam,
    isDisabled
  }: {
    exam: {
      id: number;
      name: string;
      timeLimit: number;
      isCompleted: boolean;
      score?: number;
    };
    onStartExam: (examId: number) => void;
    isDisabled: boolean;

  }) => (
    <div className="overflow-hidden relative bg-white rounded-lg border border-gray-200 transition-all duration-300 group hover:border-blue-300 hover:shadow-md">
      <div className="p-4 md:p-5">
        <div className="flex flex-col md:flex-row justify-between items-start gap-4 md:gap-0">
          <div className="flex-1">
            <div className="flex items-center mb-2 space-x-2">
              <h4 className="font-semibold text-gray-900">{exam.name}</h4>
              <div className={`w-2 h-2 rounded-full ${exam.isCompleted ? 'bg-green-500' : 'bg-orange-500'}`}></div>
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              {exam.timeLimit > 0 && (
                <span className="flex items-center">
                  <Clock className="mr-1 w-4 h-4" />
                  {exam.timeLimit} min
                </span>
              )}


            </div>
          </div>

          <div className="flex items-center justify-end md:justify-start space-x-2 w-full md:w-auto mt-4 md:mt-0">
            {exam.isCompleted ? (
              <span className="px-4 py-2 text-sm text-gray-600">Completed</span>
            ) : (
              <button
                onClick={() => onStartExam(exam.id)}
                disabled={isDisabled}
                className={`flex items-center px-4 py-2 space-x-1 text-sm text-white rounded-lg transition-all ${isDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'}`}
              >
                <Play className="w-4 h-4" />
                <span>Start Exam</span>
              </button>
            )}
          </div>
        </div>
      </div>


    </div>
  );




  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <div className="p-4 md:p-6 mx-auto max-w-7xl">
        {/* Header with Stats */}
        <div className="mb-10">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="mb-2 text-2xl md:text-4xl font-bold text-gray-900">Self Discovery Suite</h1>
              <p className="text-lg text-gray-600">Test Beyond Boundaries Reveal the Real you..</p>
            </div>


          </div>
        </div>

        {/* Current Package - Enhanced Design */}
        {currentPackage && (
          <div className="mb-12">
            <h2 className="flex items-center mb-6 text-2xl font-semibold text-gray-900">
              <Zap className="mr-2 w-6 h-6 text-blue-600" />
              Active Package
            </h2>

            <div className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-lg">
              <div className="relative p-4 md:p-8 text-white bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br to-transparent from-white/20"></div>
                </div>

                <div className="relative z-10">
                  <div className="flex flex-col md:flex-row justify-between items-start mb-6">
                    <div>
                      <h3 className="mb-3 text-xl md:text-3xl font-bold">{currentPackage?.packageName}</h3>
                    </div>
                    <div className="text-left md:text-right mt-2 md:mt-0">
                      <div className="mb-1 text-3xl md:text-4xl font-bold">₹{currentPackage?.packagePrice}/-</div>
                      <div className="text-blue-200">Active since {formatDate(currentPackage?.startedDate || "")}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 md:p-8">
                <div className="grid grid-cols-1 gap-8">
                  {/* Exams Section */}
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h4 className="flex items-center text-xl font-semibold text-gray-900">
                        <Users className="mr-2 w-5 h-5" />
                        Your Assessments
                      </h4>
                      {currentPackage && currentPackage.exams.length > 0 && currentPackage.exams.every(exam => exam.isCompleted) && (
                        <button
                          onClick={() => handleResult(currentPackage.id)}
                          className="px-4 py-2 text-sm text-blue-500 rounded border border-blue-500 hover:bg-blue-500 hover:text-white"
                        >
                          View Result
                        </button>
                      )}
                    </div>
                    <div className="space-y-4">
                      {currentPackage?.exams?.map((exam, index) => {
                        const previousExamCompleted = index === 0 ? true : currentPackage?.exams[index - 1]?.isCompleted;
                        const isExamDisabled = !previousExamCompleted;

                        return (
                          <ExamStatus
                            key={exam.examId}
                            exam={{
                              id: exam.examId,
                              name: exam.examName,
                              timeLimit: exam.timeLimit,
                              isCompleted: exam.isCompleted,
                            }}
                            onStartExam={handleQuizClick}
                            isDisabled={isExamDisabled}
                          />
                        );
                      })}
                    </div>
                  </div>


                </div>
              </div>
            </div>
          </div>
        )}

        {/* Completed Packages Section */}
        {completedPackages && completedPackages.length > 0 && (
          <div className="mb-12">
            <h2 className="flex items-center mb-6 text-2xl font-semibold text-gray-900">
              <CheckCircle className="mr-2 w-6 h-6 text-green-600" />
              Completed Packages
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {(showAllCompleted ? completedPackages : completedPackages.slice(0, 3)).map((pkg) => (
                <div key={pkg.id} className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-lg">
                  <div className="relative p-4 md:p-6 text-white bg-gradient-to-r from-green-600 via-green-700 to-emerald-700">
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br to-transparent from-white/20"></div>
                    </div>
                    <div className="relative z-10">
                      <h3 className="mb-2 text-2xl font-bold">{pkg.packageName}</h3>
                      <div className="text-green-200">Completed on {formatDate(pkg.startedDate || "")}</div>
                    </div>
                  </div>
                  <div className="p-4 md:p-6">
                    <h4 className="flex items-center mb-4 text-lg font-semibold text-gray-900">
                      <Award className="mr-2 w-5 h-5" />
                      Assessments
                    </h4>
                    <div className="space-y-3">
                      {pkg.exams?.map((exam, index) => {
                        const previousExamCompleted = index === 0 ? true : pkg.exams[index - 1]?.isCompleted;
                        const isExamDisabled = !previousExamCompleted;

                        return (
                          <ExamStatus
                            key={exam.examId}
                            exam={{
                              id: exam.examId,
                              name: exam.examName,
                              timeLimit: exam.timeLimit,
                              isCompleted: exam.isCompleted,
                            }}
                            onStartExam={handleQuizClick}
                            isDisabled={isExamDisabled}
                          />
                        );
                      })}
                    </div>
                    {pkg.exams.every(exam => exam.isCompleted) && (
                      <button
                        onClick={() => handleResult(pkg.id)}
                        className="px-4 py-2 mt-6 w-full text-sm text-white bg-green-500 rounded-lg hover:bg-green-600"
                      >
                        View Result
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {completedPackages.length > 3 && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => setShowAllCompleted(!showAllCompleted)}
                  className="px-6 py-2.5 text-sm font-medium text-green-700 bg-green-50 rounded-full border border-green-200 hover:bg-green-100 transition-colors duration-200 flex items-center"
                >
                  {showAllCompleted ? (
                    <>
                      Show Less <span className="ml-2">↑</span>
                    </>
                  ) : (
                    <>
                      View All History ({completedPackages.length}) <span className="ml-2">↓</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Available Packages - Grid Layout */}
        <div>
          <div className="mb-10 text-center">
            <h2 className="mb-3 text-3xl font-bold text-gray-900">Upgrade Your Learning</h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Explore our comprehensive assessment packages designed to unlock your potential
            </p>
          </div>
          <div className="mt-4 mb-6 text-center">
            <h3 className="text-2xl font-bold text-gray-900">
              Select Test Package/s From Below:
            </h3>
          </div>
          <AvailablePackagesSection />
        </div>
      </div>
    </div >
  );
};

export default HomeComponent;
